import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, FormControl, InputLabel, Select, MenuItem, Alert, CircularProgress, Box, Typography, Chip } from '@mui/material';
import Grid from '@mui/material/Grid';

import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ro } from 'date-fns/locale';
import {
  appointmentsService,
  CreateAppointmentData,
  Service,
  Doctor,
  Location,
  Room
} from '@services/appointmentsService';
import { useAuth } from '@contexts/AuthContext';

interface CreateAppointmentDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  petId: string;
  ownerId: string;
  petName: string;
}

const CreateAppointmentDialog: React.FC<CreateAppointmentDialogProps> = ({
  open,
  onClose,
  onSuccess,
  petId,
  ownerId,
  petName
}) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [conflictWarning, setConflictWarning] = useState<string | null>(null);
  
  // Form data
  const [formData, setFormData] = useState({
    serviceId: '',
    doctorId: '',
    locationId: '',
    roomId: '',
    start: new Date(),
    notes: ''
  });

  // Options
  const [services, setServices] = useState<Service[]>([]);
  const [doctors, setDoctors] = useState<Doctor[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [rooms, setRooms] = useState<Room[]>([]);
  const [selectedService, setSelectedService] = useState<Service | null>(null);
  
  // No-show statistics
  const [noShowStats, setNoShowStats] = useState<any>(null);

  useEffect(() => {
    if (open && user?.clinicId) {
      loadOptions();
      loadNoShowStats();
    }
  }, [open, user?.clinicId, ownerId]);

  useEffect(() => {
    if (formData.serviceId) {
      const service = services.find(s => s.id === formData.serviceId);
      setSelectedService(service || null);
    }
  }, [formData.serviceId, services]);

  useEffect(() => {
    if (formData.locationId) {
      const location = locations.find(l => l.id === formData.locationId);
      setRooms(location?.rooms || []);
      setFormData(prev => ({ ...prev, roomId: '' }));
    }
  }, [formData.locationId, locations]);

  useEffect(() => {
    if (formData.doctorId && formData.roomId && formData.start) {
      checkConflicts();
    }
  }, [formData.doctorId, formData.roomId, formData.start]);

  const loadOptions = async () => {
    if (!user?.clinicId) return;

    try {
      setLoading(true);
      const [servicesData, doctorsData, locationsData] = await Promise.all([
        appointmentsService.getServices(user.clinicId),
        appointmentsService.getDoctors(user.clinicId),
        appointmentsService.getLocations(user.clinicId)
      ]);

      setServices(servicesData);
      setDoctors(doctorsData);
      setLocations(locationsData);

      // Set default doctor to current user if they are a doctor
      if (doctorsData.length > 0) {
        const currentUserDoctor = doctorsData.find(d => d.id === user.id);
        if (currentUserDoctor) {
          setFormData(prev => ({ ...prev, doctorId: currentUserDoctor.id }));
        }
      }
    } catch (error) {
      console.error('Error loading options:', error);
      setError('Eroare la incarcarea optiunilor');
    } finally {
      setLoading(false);
    }
  };

  const loadNoShowStats = async () => {
    if (!ownerId) return;
    
    try {
      const response = await appointmentsService.getClientNoShowStats(ownerId);
      setNoShowStats(response.data);
    } catch (error) {
      console.error('Error loading no-show stats:', error);
      // Nu afisam eroarea, doar nu incarcam statisticile
    }
  };

  const checkConflicts = async () => {
    if (!formData.doctorId || !formData.start) return;

    try {
      const duration = selectedService ? selectedService.duration : 30;
      const endTime = new Date(formData.start.getTime() + duration * 60000);
      const conflicts = await appointmentsService.checkConflicts({
        doctorId: formData.doctorId,
        roomId: formData.roomId || undefined,
        start: formData.start.toISOString(),
        end: endTime.toISOString()
      });

      if (conflicts.hasConflict) {
        setConflictWarning(`Conflict detectat: ${conflicts.conflicts.length} programari suprapuse`);
      } else {
        setConflictWarning(null);
      }
    } catch (error) {
      console.error('Error checking conflicts:', error);
    }
  };

  const handleSubmit = async () => {
    if (!user?.clinicId) return;

    try {
      setLoading(true);
      setError(null);

      // Use default duration of 30 minutes if no service is selected
      const duration = selectedService ? selectedService.duration : 30;
      const endTime = new Date(formData.start.getTime() + duration * 60000);
      
      const appointmentData: CreateAppointmentData = {
        clinicId: user.clinicId,
        locationId: formData.locationId || undefined,
        roomId: formData.roomId || undefined,
        doctorId: formData.doctorId,
        clientId: ownerId,
        patientId: petId,
        serviceId: formData.serviceId || undefined,
        start: formData.start.toISOString(),
        end: endTime.toISOString(),
        notes: formData.notes
      };

      await appointmentsService.createAppointment(appointmentData);
      
      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('Error creating appointment:', error);
      setError(error.response?.data?.error || 'Eroare la crearea programarii');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      serviceId: '',
      doctorId: '',
      locationId: '',
      roomId: '',
      start: new Date(),
      notes: ''
    });
    setError(null);
    setConflictWarning(null);
    setSelectedService(null);
    onClose();
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ro}>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>
          Creeaza Programare pentru {petName}
        </DialogTitle>
        <DialogContent>
          {loading && (
            <Box display="flex" justifyContent="center" p={2}>
              <CircularProgress />
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {conflictWarning && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {conflictWarning}
            </Alert>
          )}

          {/* Statistici No-Show ale clientului */}
          {noShowStats && noShowStats.totalNoShows > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                ⚠️ Atenție: Acest client are {noShowStats.totalNoShows} no-show-uri
              </Typography>
              <Typography variant="body2">
                • Ultimele 30 zile: {noShowStats.noShowsLast30Days} no-show-uri<br/>
                • Ultimele 7 zile: {noShowStats.noShowsLast7Days} no-show-uri<br/>
                {noShowStats.noShowPercentage > 0 && (
                  <>• Procent no-show: {noShowStats.noShowPercentage.toFixed(1)}%</>
                )}
              </Typography>
            </Alert>
          )}

          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Serviciu (optional)</InputLabel>
                <Select
                  value={formData.serviceId}
                  onChange={(e) => setFormData(prev => ({ ...prev, serviceId: e.target.value }))}
                  label="Serviciu (optional)"
                >
                  <MenuItem value="">
                    <em>Fara serviciu specificat</em>
                  </MenuItem>
                  {services.map((service) => (
                    <MenuItem key={service.id} value={service.id}>
                      {service.name} ({service.duration} min)
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth required>
                <InputLabel>Medic</InputLabel>
                <Select
                  value={formData.doctorId}
                  onChange={(e) => setFormData(prev => ({ ...prev, doctorId: e.target.value }))}
                  label="Medic"
                >
                  {doctors.map((doctor) => (
                    <MenuItem key={doctor.id} value={doctor.id}>
                      {doctor.firstName} {doctor.lastName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Locatie (optional)</InputLabel>
                <Select
                  value={formData.locationId}
                  onChange={(e) => setFormData(prev => ({ ...prev, locationId: e.target.value }))}
                  label="Locatie (optional)"
                >
                  <MenuItem value="">
                    <em>Fara locatie specificata</em>
                  </MenuItem>
                  {locations.map((location) => (
                    <MenuItem key={location.id} value={location.id}>
                      {location.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Sala (optional)</InputLabel>
                <Select
                  value={formData.roomId}
                  onChange={(e) => setFormData(prev => ({ ...prev, roomId: e.target.value }))}
                  label="Sala (optional)"
                  disabled={!formData.locationId}
                >
                  <MenuItem value="">
                    <em>Fara sala specificata</em>
                  </MenuItem>
                  {rooms.map((room) => (
                    <MenuItem key={room.id} value={room.id}>
                      {room.name} (Capacitate: {room.capacity})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <DateTimePicker
                label="Data si ora"
                value={formData.start}
                onChange={(newValue) => {
                  if (newValue) {
                    setFormData(prev => ({ ...prev, start: newValue }));
                  }
                }}
                minutesStep={30}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    required: true
                  }
                }}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              {selectedService && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Durata estimata
                  </Typography>
                  <Chip 
                    label={`${selectedService.duration} minute`} 
                    color="primary" 
                    size="small" 
                  />
                </Box>
              )}
            </Grid>

            <Grid size={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Note"
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Note suplimentare pentru programare..."
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>
            Anuleaza
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            disabled={loading || !formData.doctorId || !formData.start}
          >
            {loading ? <CircularProgress size={20} /> : 'Creeaza Programare'}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default CreateAppointmentDialog;
